import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import {
  Sparkles,
  Download,
  Palette,
  Zap,
  Shield,
  ArrowRight,
  CheckCircle2,
  Star
} from "lucide-react";

const Landing = () => {
  const features = [
    {
      icon: <Palette className="w-6 h-6" />,
      title: "24+ Premium Templates",
      description: "Professionally designed templates for every industry and style."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "AI-Powered Design",
      description: "Smart suggestions for colors, fonts, and layouts tailored to your brand."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Real-time Editor",
      description: "See changes instantly with our live preview editor."
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: "Print-Ready Export",
      description: "Download in PNG or PDF format, ready for professional printing."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Print-Safe Margins",
      description: "Built-in bleed areas ensure your cards print perfectly every time."
    },
    {
      icon: <Star className="w-6 h-6" />,
      title: "Zero Learning Curve",
      description: "Simple interface. Create stunning cards in minutes, not hours."
    }
  ];

  const steps = [
    { step: "1", title: "Choose Template", desc: "Browse our premium collection" },
    { step: "2", title: "Customize", desc: "Add your details & style" },
    { step: "3", title: "Download", desc: "Get print-ready files" }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-heading text-xl font-bold tracking-tight">
            CardStudio
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/templates">
              <Button variant="ghost" className="rounded-full" data-testid="nav-templates-btn">
                Templates
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="nav-get-started-btn">
                My Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-secondary rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4 text-amber-500" />
                AI-Powered Design Assistant
              </span>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-none mb-6">
                Create Stunning
                <br />
                <span className="bg-gradient-to-r from-amber-500 to-orange-500 bg-clip-text text-transparent">Business Cards</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-lg mb-8 leading-relaxed">
                Design professional business cards in minutes. Choose from 24+ premium templates,
                customize with AI-powered suggestions, and download print-ready files.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/templates">
                  <Button
                    size="lg"
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-8 text-base font-medium transition-transform active:scale-95"
                    data-testid="hero-start-designing-btn"
                  >
                    Start Designing
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/templates">
                  <Button
                    size="lg"
                    variant="outline"
                    className="rounded-full h-14 px-8 text-base font-medium"
                    data-testid="hero-browse-templates-btn"
                  >
                    Browse Templates
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-muted-foreground">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  Free to start
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  No credit card
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              {/* Card Preview Stack */}
              <div className="relative h-[400px] lg:h-[500px]">
                {/* Background card */}
                <div className="absolute top-12 left-12 w-[340px] h-[200px] bg-gradient-to-br from-zinc-800 to-zinc-900 rounded-2xl shadow-2xl transform rotate-6" />

                {/* Middle card */}
                <div className="absolute top-6 left-6 w-[340px] h-[200px] bg-white border border-border rounded-2xl shadow-xl transform rotate-3 p-6">
                  <div className="h-full flex flex-col justify-between">
                    <div>
                      <div className="w-8 h-8 bg-[#2563EB] rounded-lg mb-4" />
                      <p className="text-xs text-muted-foreground">CREATIVE DIRECTOR</p>
                    </div>
                    <div>
                      <p className="font-heading text-xl font-bold">Sarah Mitchell</p>
                      <p className="text-sm text-muted-foreground">hello@studio.co</p>
                    </div>
                  </div>
                </div>

                {/* Front card */}
                <div className="absolute top-0 left-0 w-[340px] h-[200px] bg-black text-white rounded-2xl shadow-2xl p-6">
                  <div className="h-full flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                        <span className="text-black font-bold text-lg">M</span>
                      </div>
                      <p className="text-xs text-zinc-400">CEO & FOUNDER</p>
                    </div>
                    <div>
                      <p className="font-heading text-2xl font-bold mb-1">Michael Chen</p>
                      <p className="text-sm text-zinc-400">michael@acme.co</p>
                      <p className="text-sm text-zinc-400">+1 (555) 123-4567</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Three Steps to Perfect Cards
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              No design skills needed. Our intuitive process gets you from idea to print-ready in minutes.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-8 border border-border hover:border-primary/20 hover:shadow-lg transition-all duration-300">
                  <span className="inline-flex items-center justify-center w-12 h-12 bg-primary text-primary-foreground rounded-full text-xl font-bold mb-6">
                    {item.step}
                  </span>
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-muted-foreground">{item.desc}</p>
                </div>
                {index < 2 && (
                  <ArrowRight className="hidden md:block absolute top-1/2 -right-4 w-8 h-8 text-muted-foreground/30 transform -translate-y-1/2" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Everything You Need
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Professional features without the complexity. Create cards that make lasting impressions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group p-6 rounded-2xl border border-border hover:border-primary/20 hover:shadow-lg transition-all duration-300 bg-white"
              >
                <div className="w-12 h-12 bg-secondary rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                  {feature.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-primary text-primary-foreground rounded-3xl p-12 md:p-16 text-center"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
              Ready to Create Your Card?
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8 max-w-xl mx-auto">
              Join thousands of professionals who trust CardStudio for their business cards.
            </p>
            <Link to="/dashboard">
              <Button
                size="lg"
                className="rounded-full bg-white text-primary hover:bg-white/90 h-14 px-10 text-base font-medium"
                data-testid="cta-create-free-btn"
              >
                Create Your Card Free
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="font-heading text-lg font-bold">CardStudio</p>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} CardStudio. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
